import React, { useState, useEffect } from 'react';
import { ClipboardList, Search, Filter, Eye, Calendar, User, Award, TrendingUp, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';
import AvaliacaoDetalhesModal from '../components/AvaliacaoDetalhesModal';

interface AvaliacaoComDetalhes {
  id: string;
  alunoId: string;
  professorId: string;
  nivelId: string;
  proximoNivelId?: string;
  dataAvaliacao: string;
  notaGeral: number;
  aprovado: boolean;
  comentario?: string;
  mudancas?: string;
  createDate: string;
  updateDate?: string;
  deleteDate?: string;
  aluno?: {
    id: string;
    nome: string;
    email: string;
    fotoUrl?: string;
  } | null;
  professor?: {
    id: string;
    nome: string;
    email: string;
  } | null;
  nivel?: {
    id: string;
    nome: string;
  } | null;
  proximoNivel?: {
    id: string;
    nome: string;
  } | null;
}

const Avaliacoes: React.FC = () => {
  const { userProfile } = useAuth();
  const [avaliacoes, setAvaliacoes] = useState<AvaliacaoComDetalhes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAvaliacao, setSelectedAvaliacao] = useState<AvaliacaoComDetalhes | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    nivel: 'all',
    professor: 'all'
  });
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'dataAvaliacao', direction: 'desc' });

  useEffect(() => {
    const loadAvaliacoes = async () => {
      if (!userProfile?.empresaCnpj) return;
      
      try {
        setLoading(true);
        setError(null);
        const avaliacoesData = await firebaseService.getAvaliacoesComDetalhes(userProfile.empresaCnpj);
        setAvaliacoes(avaliacoesData);
      } catch (err: any) {
        setError('Erro ao carregar avaliações');
        console.error('Erro ao carregar avaliações:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAvaliacoes();
  }, [userProfile?.empresaCnpj]);

  // Filtrar e ordenar avaliações
  const filteredAndSortedAvaliacoes = avaliacoes.filter(avaliacao => {
    // Filtro de busca por texto
    const matchesSearch = searchTerm === '' || 
      (avaliacao.aluno?.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (avaliacao.professor?.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (avaliacao.nivel?.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (avaliacao.comentario || '').toLowerCase().includes(searchTerm.toLowerCase());

    // Filtro de nível
    const matchesNivel = filters.nivel === 'all' || avaliacao.nivelId === filters.nivel;

    // Filtro de professor
    const matchesProfessor = filters.professor === 'all' || avaliacao.professorId === filters.professor;

    return matchesSearch && matchesNivel && matchesProfessor;
  }).sort((a, b) => {
    if (!sortConfig) return 0;
    
    const { key, direction } = sortConfig;
    let aValue: any = a[key as keyof AvaliacaoComDetalhes];
    let bValue: any = b[key as keyof AvaliacaoComDetalhes];
    
    // Tratamento especial para diferentes tipos de dados
    if (key === 'dataAvaliacao' || key === 'createDate') {
      aValue = new Date(aValue || 0).getTime();
      bValue = new Date(bValue || 0).getTime();
    } else if (key === 'notaGeral') {
      aValue = aValue || 0;
      bValue = bValue || 0;
    } else {
      aValue = (aValue || '').toString().toLowerCase();
      bValue = (bValue || '').toString().toLowerCase();
    }
    
    if (aValue < bValue) {
      return direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig?.key === key) {
        return {
          key,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleViewDetails = (avaliacao: AvaliacaoComDetalhes) => {
    setSelectedAvaliacao(avaliacao);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAvaliacao(null);
  };

  // Estatísticas
  const totalAvaliacoes = avaliacoes.length;
  const mediaPorcentagem = avaliacoes.length > 0
    ? avaliacoes.reduce((sum, a) => sum + ((a.notaGeral || 0) * 100), 0) / avaliacoes.length
    : 0;

  // Obter lista única de níveis e professores para filtros
  const niveisUnicos = Array.from(new Set(avaliacoes.map(a => a.nivelId)))
    .map(nivelId => {
      const nivel = avaliacoes.find(a => a.nivelId === nivelId)?.nivel;
      return { id: nivelId, nome: nivel?.nome || 'Nível não encontrado' };
    });

  const professoresUnicos = Array.from(new Set(avaliacoes.map(a => a.professorId)))
    .map(professorId => {
      const professor = avaliacoes.find(a => a.professorId === professorId)?.professor;
      return { id: professorId, nome: professor?.nome || 'Professor não encontrado' };
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
          <div className="flex items-center gap-3 mb-2">
            <ClipboardList className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Avaliações</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Visualize as avaliações realizadas pelos professores para acompanhar o progresso dos alunos
          </p>
        </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total de Avaliações</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">{totalAvaliacoes}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Média de Porcentagem</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
                  {mediaPorcentagem.toFixed(1)}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por aluno, professor, nível ou comentário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-md border transition-colors ${
                showFilters
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              <Filter className="w-4 h-4 inline mr-2" />
              Filtros
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nível
                </label>
                <select
                  value={filters.nivel}
                  onChange={(e) => setFilters({ ...filters, nivel: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                >
                  <option value="all">Todos</option>
                  {niveisUnicos.map(nivel => (
                    <option key={nivel.id} value={nivel.id}>{nivel.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Professor
                </label>
                <select
                  value={filters.professor}
                  onChange={(e) => setFilters({ ...filters, professor: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                >
                  <option value="all">Todos</option>
                  {professoresUnicos.map(professor => (
                    <option key={professor.id} value={professor.id}>{professor.nome}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('dataAvaliacao')}
                  >
                    <div className="flex items-center gap-2">
                      Data
                      {sortConfig?.key === 'dataAvaliacao' && (
                        <span className="text-primary-600 dark:text-primary-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aluno
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Professor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Nível
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort('notaGeral')}
                  >
                    <div className="flex items-center gap-2">
                      Porcentagem
                      {sortConfig?.key === 'notaGeral' && (
                        <span className="text-primary-600 dark:text-primary-400">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAndSortedAvaliacoes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      {loading ? (
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      ) : (
                        'Nenhuma avaliação encontrada'
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedAvaliacoes.map((avaliacao) => (
                    <tr
                      key={avaliacao.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {new Date(avaliacao.dataAvaliacao).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {avaliacao.aluno?.fotoUrl ? (
                            <img
                              src={avaliacao.aluno.fotoUrl}
                              alt={avaliacao.aluno.nome}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                              <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {avaliacao.aluno?.nome || 'Aluno não encontrado'}
                            </div>
                            {avaliacao.aluno?.email && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {avaliacao.aluno.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {avaliacao.professor?.nome || 'Professor não encontrado'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {avaliacao.nivel?.nome || 'Nível não encontrado'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        <span className="font-semibold">{((avaliacao.notaGeral || 0) * 100).toFixed(1)}%</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewDetails(avaliacao)}
                          className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          Ver Detalhes
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      </div>

      {/* Modal de Detalhes */}
      {isModalOpen && selectedAvaliacao && (
        <AvaliacaoDetalhesModal
          avaliacao={selectedAvaliacao}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default Avaliacoes;

